<template>
	<div class="fd-adr-container">
		<lines-area v-model="streets" :max-lines="3" class="fd-adr-streets" :style="areaTags" :placeholder="env.msg('customer.street')" :readonly="ro"></lines-area>
		<a :href="mapUrl" class="fd-adr-place" target="_blank"><icon-button name="location"></icon-button></a>
		<input type="text" v-model="zip" class="fd-adr-zip tn" :placeholder="env.msg('customer.zip')" :readonly="ro"/>
		<input type="text" v-model="city" class="fd-adr-city" :placeholder="env.msg('customer.city')" :readonly="ro"/>

		<span v-if="env.touch" class="fd-adr-country" @click.prevent.stop="pickCountry">{{ value.country }}</span>
		<span v-else class="fd-adr-country"><more-opener v-model="openMore"></more-opener></span>

		<show-down class="fd-adr-more fd-small" :show="openMore">
			<div class="fd-adr-country-state">
				<select class="fd-adr-country-select" v-model="value.country" :disabled="ro">
					<option v-for="li in countryListItems" :value="li.value">{{ li.name }}</option>
				</select>
				<input class="fd-adr-state" type="text" v-model="value.state" :placeholder="env.msg('customer.state')" :readonly="ro"/>
			</div>
			<div class="fd-adr-flags">
				<label><input type="checkbox" class="fd-checkbox" v-model="value.home" :disabled="ro"> {{ env.msg('customer.homeAddr') }}</label>
				<label><input type="checkbox" class="fd-checkbox" v-model="value.invoice" :disabled="ro"> {{ env.msg('customer.invoiceAddr') }}</label>
				<label><input type="checkbox" class="fd-checkbox" v-model="value.delivery" :disabled="ro"> {{ env.msg('customer.deliveryAddr') }}</label>
				<label><input type="checkbox" class="fd-checkbox" v-model="value.mailing" :disabled="ro"> {{ env.msg('customer.mailingAddr') }}</label>
			</div>
			<div class="fd-adr-gps">
				<label class="fd-label">{{ env.msg('customer.gpsInfo') }}:</label>
				<span><input type="text" v-model.number="value.lat" :readonly="ro" style="margin-right: var(--input-spacing)"><input type="text"
					v-model.number="value.lng" :readonly="ro"></span>
			</div>
		</show-down>
	</div>
</template>

<script setup lang="ts">
import LinesArea from "./LinesArea.vue";
import useEditor, {type EditorProps, editorProps} from "../Editor";
import {computed, type CSSProperties, ref} from "vue";
import {injectEnv} from "../../Environment";
import {type PanelDescriptor} from "../../Types";
import MoreOpener from "../../helpers/MoreOpener.vue";
import ShowDown from "../../helpers/ShowDown.vue";
import {injectFindList} from "../../PartInjectKeys";
import IconButton from "../../helpers/IconButton.vue";
import { onMounted } from "vue";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;

const env = injectEnv();

const openMore = ref<boolean>(false);

const {visible, value, clazz, style, ro} = useEditor(props);
const findList = injectFindList();
const countryListItems = findList("countryList").value;

const homeIcon = ref<string>(null);
const invoiceIcon = ref<string>(null);
const deliveryIcon = ref<string>(null);
const mailingIcon = ref<string>(null);

const streets = computed({
    get() {
        const v = value.value.streets;
        if (Array.isArray(v)) return v;
        return [""];
    },
    set(v) {
        value.value.streets = v;
    },
});
const zip = computed({
    get() {
        const v = value.value.zip;
        return typeof v === "string" ? v : "";
    },
    set(v) {
        value.value.zip = v;
    },
});
const city = computed({
    get() {
        const v = value.value.city;
        return typeof v === "string" ? v : "";
    },
    set(v) {
        value.value.city = v;
    },
});
const mapUrl = computed(() => {
    const url = generateMapUrl(value.value.streets, value.value.city, value.value.lat, value.value.lng);
    if (url) return url;
    return "#";
});

const areaTags = computed<CSSProperties>(() => {
    const s: CSSProperties = {};
    const icons = [];
    const d = value.value;
    if (d.home && homeIcon.value) icons.push(homeIcon.value);
    if (d.invoice && invoiceIcon.value) icons.push(invoiceIcon.value);
    if (d.delivery && deliveryIcon.value) icons.push(deliveryIcon.value);
    if (d.mailing && mailingIcon.value) icons.push(mailingIcon.value);
    if (icons.length === 0) s.backgroundImage = "none";
    else {
        const urls = [];
        const repeats = [];
        const sizes = [];
        const positions = [];
        let offset = (icons.length - 1) * 18;
        for (let icon of icons) {
            urls.push('url(' + icon + ')')
            repeats.push("no-repeat");
            sizes.push("18px 18px");
            positions.push("right " + (offset) + "px top 2px");
            offset -= 18;
        }
        s.backgroundImage = urls.join(",");
        s.backgroundRepeat = repeats.join(",");
        s.backgroundSize = sizes.join(",");
        s.backgroundPosition = positions.join(",");
    }
	//console.log(JSON.stringify(s));
    return s;
});

function pickCountry() {
    env.loadList("_ctr")
        .then((list) => {
            const pg: PanelDescriptor = {
                component: "list-select-page",
                bind: {
                    list: list,
                    selected: value.value.country,
                    multi: false,
                    pageTitle: env.msg("list.title.country"),
                }
            };
            return env.showPanel(pg);
        }).then((result) => {
        if (result !== undefined) value.value.country = result;
    });
}

function generateMapUrl(streets: string | string[], city: string | null, lat?: number, lng?: number) {
	const baseUrl = 'https://www.google.com/maps/search/?api=1';

	if (lat !== undefined && lng !== undefined && lat !== 0 && lng !== 0) {
		return `${baseUrl}&query=${lat},${lng}`;
	}

	let query = '';
	if (Array.isArray(streets)) {
		query = streets.join(' ');
	} else {
		query = streets;
	}

	if (city) {
		query = `${query} ${city}`;
	}

	query = encodeURIComponent(query.trim());

	return `${baseUrl}&query=${query}`;
}

onMounted(() => {

	if (import.meta.env.DEV) {
		homeIcon.value = new URL("../../assets/for-home.svg", import.meta.url).href;
		invoiceIcon.value = new URL("../../assets/for-invoice.svg", import.meta.url).href;
		deliveryIcon.value = new URL("../../assets/for-delivery.svg", import.meta.url).href;
		mailingIcon.value = new URL("../../assets/for-mailing.svg", import.meta.url).href;
	} else if (import.meta.env.PROD) {
		homeIcon.value = new URL(/* @vite-ignore */ "../img/for-home.svg", import.meta.url).href;
		invoiceIcon.value = new URL(/* @vite-ignore */ "../img/for-invoice.svg", import.meta.url).href;
		deliveryIcon.value = new URL(/* @vite-ignore */ "../img/for-delivery.svg", import.meta.url).href;
		mailingIcon.value = new URL(/* @vite-ignore */ "../img/for-mailing.svg", import.meta.url).href;
	}
});
</script>

<style>
.fd-adr-container {
	display: grid;
	align-items: baseline;
	justify-items: stretch;
	grid-template-columns: [adr-start] minmax(60px, 1fr) [adr-zip-end] minmax(100px, 3fr) [adr-end] 22px [country-end];
	grid-gap: 4px;
}

.fd-adr-streets {
	grid-column: adr-start / adr-end;
}

.fd-touch .fd-adr-streets {
	border-bottom: var(--hair) solid var(--border-color);
}


.fd-adr-place {
	grid-column: adr-end / country-end;
	display: flex;
	align-items: stretch;
	justify-content: flex-start;
	vertical-align: middle;
	box-sizing: border-box;
	--link-decoration: none;
	--link-decoration-hover: none;
}

.fd-touch .fd-adr-place {
	padding-top: 8px;
	border-left: var(--hair) solid var(--border-color) !important;
	border-bottom: var(--hair) solid var(--border-color);
}

.fd-adr-zip {
	grid-column: adr-start / adr-zip-end;
}

.fd-adr-city {
	grid-column: adr-zip-end / adr-end;
}

.fd-touch .fd-adr-city {
	border-left: var(--hair) solid var(--border-color) !important;
	padding-left: var(--gap) !important;
}

.fd-adr-country {
	grid-column: adr-end / country-end;
}

.fd-touch .fd-adr-country {
	border-left: var(--hair) solid var(--border-color) !important;
	padding-top: 8px;
	text-align: center;
}

.fd-adr-more {
	grid-column: adr-start / adr-end;
	display: flex;
	flex-direction: column;
	row-gap: 4px;
	padding: var(--form-v-padding) 0;
}

.fd-adr-flags {
	width: 100%;
	display: flex;
	justify-content: space-between;
}

.fd-adr-gps {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: baseline;
	column-gap: var(--input-spacing);

	& input {
		width: 72px;
	}
}

.fd-adr-country-state {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
}

.fd-adr-country-select {
	width: calc(50% - 2px);
}

.fd-adr-state {
	width: calc(50% - 2px);
}
</style>
